import React from "react";

import "./Blog.css";
import NavLight from "../../components/NavLight";
import BlogPageIntroImg from "../../assets/blog/discovery_interviews_ask_the_right_customer_interview_questions.png";
import BlogsPreview from "../../components/BlogsPreview";
import FoundersGuide from "../../components/FoundersGuide";
import {CustomMetaHelmet} from "../../components/CustomMetaHelmet";
import {meta} from "../../assets/meta/meta";

function HowToDiscoverInterviewsPart2() {
  return (
    <div className="Blog1">
      <CustomMetaHelmet meta={meta['how-to-do-discovery-interviews-customer-interview-questions']}/>
      <NavLight />
      <section className="blog-page">
        <article className="blog-page-container">
          <div>
            <h1>
              How to do discovery interviews. <br /> Part 2: Ask the right customer
              interview questions
            </h1>
            <p>
              <i>
                A successful discovery interview can lead to pivotal, unique
                insights about your ideal customer profile, their business
                problems, and priorities. In many cases, these interviews can
                make or break a successful launch of a product or improvement to
                an existing one. In this guide, I will go through how to be
                successful in discovery interviews by asking the right customer
                interview questions.
              </i>
            </p>
          </div>
          <div>
            <figure>
              <img
                src={BlogPageIntroImg}
                alt="purple hand pressing a digital button"
              />
            </figure>
          </div>
          <div>
            <h2>Preparation for discovery interview</h2>
            <p>
              This goes without saying: you should never enter a discovery
              meeting unprepared. Take the time to educate yourself about the
              person you are going to speak with. Research their professional
              career, job role, and background, so that you join the call with a
              decent understanding of the individual. If you are using Magic
              Feedback, you can simply go to Magic Profiles and look up any
              customer and read all the feedback that they have given you
              historically across all channels.
              <p>&nbsp;</p>
              For instance, if the person you are interviewing has recently
              started a new job, it's important to show empathy and consider
              that when formulating your questions. Look for opportunities
              rather than limitations. A person who is new to their role may be
              more willing to share challenges because they are still adapting
              to their routines. Ensure that you make use of your understanding
              of the person you are interviewing. If you're using Magic
              Profiles, you can view all the feedback that they have given
              historically across all channels. Make sure you don't overlook any
              significant feedback they've given you or one of your other
              customer-facing teams recently.
            </p>
          </div>
          <div>
            <h2>How to conduct the discovery customer interview</h2>
            <p>
              Start by expressing gratitude and thanking the customer for taking
              the time to speak with you. Sincerely convey your appreciation for
              their willingness to engage in the conversation despite their busy
              schedule. Introduce yourself in a personable manner to establish a
              connection and create a sense of psychological safety. After the
              introductions, share the agenda for the interview. I recommend
              framing it as an informal discussion where you go with the flow.
              This informality helps create a relaxed atmosphere. Consider
              yourself as a researcher or journalist who is genuinely curious
              about exploring a specific problem. Remember, the objective of
              this meeting is to validate your idea, so resist the urge to sell
              your solution, regardless of how tempting it may be during the
              conversation.
              <p>&nbsp;</p>
              For instance, most discovery customer interviews typically last
              around 30 minutes. Allocate the first 5 minutes for introductions
              to establish rapport with the person. Then, dedicate 5 minutes to
              set the context and agenda for the interview. Be mindful of time
              management throughout the conversation. Always leave around 5
              minutes at the end to express gratitude for the interview and
              inquire about the possibility of following up with a potential
              solution to their problem.
            </p>
          </div>
          <div>
            <h2>Validation of problem statement</h2>
            <p>
              First and foremost, you need to come up with one or two questions
              that can validate your problem statement with a simple yes or no.
              It's important to ask these questions at the beginning of the
              interview to determine if you are indeed speaking with your ideal
              customer profile (ICP). By ensuring that you are engaging with
              your ICP during the discovery process, you can avoid collecting
              feedback that might lead you in the wrong direction. Sometimes,
              you may discover that the customer does not fit your ICP. In such
              cases, you can ask them about their top three priorities and
              explore if there's something interesting beyond your ICP. However,
              as a general rule, to respect everyone's time, you should consider
              politely ending the meeting early if the customer does not fall
              within your ICP.
              <p>&nbsp;</p>
              For example, let's say if your product is an app to remind remote
              workers to take breaks and engage in exercises to improve their
              health. Some examples of questions that can validate your problem
              statement could be: Have you experienced any health problems since
              transitioning to remote work, such as back pain, neck pain, or
              poor circulation? Do you ever forget to take breaks or move around
              during your workday?
            </p>
          </div>
          <div>
            <h2>Discovery customer interview questions</h2>
            <p>
              Once you have validated that you are speaking with your ideal
              customer profile (ICP), your next step is to ask a series of
              discovery questions to gain a better understanding of their
              problem, priorities, and how they are currently attempting to
              solve it. It is advisable to avoid asking "would you" questions,
              closed questions, leading questions, or asking directly what
              people want. Instead, focus on asking questions that help you
              comprehend the customer's experiences and how the problem impacts
              their daily life.
              <p>&nbsp;</p>
              For example, if we continue with the example of building an app
              for remote workers to incorporate exercise and improve their
              health, you could ask questions such as: Can you describe a
              typical workday for you as a remote worker? How significant is
              physical activity and movement for you during your workday? How do
              you remind yourself to take breaks or include physical activity in
              your workday?
            </p>
          </div>
          <div>
            <h2>Follow up questions</h2>
            <p>
              Once you begin asking the customer the prepared discovery
              questions, it's important to practise and skillfully ask follow-up
              questions. More often than not, it is these follow-up questions
              that provide unique insights, helping you validate your idea.
              Consider the initial discovery questions as a means to encourage
              the person to open up broadly about the problem, while the
              follow-up questions enable you to laser-focus on validating
              specific aspects of the problem that your idea aims to address.
              <p>&nbsp;</p>
              For example, during a discovery customer interview, you might ask
              the customer to describe a typical day as a remote worker. If the
              customer briefly mentions going for a quick walk after lunch, as
              you are building an app to remind remote workers to exercise for
              health purposes, this presents an opportunity to ask a series of
              follow-up questions. You can inquire about what motivates them to
              take their daily walks after lunch and how they remind themselves
              to do it. Depending on their answers, you can further explore with
              additional relevant follow-up questions to gain deeper insights.
              Train yourself to use "what," "how," and "why" questions to elicit
              responses from the customer that offer richer details compared to
              closed questions.
            </p>
          </div>
          <div>
            <h2>Write down rich notes</h2>
            <p>
              During the discovery interview, your ideal customer profile will
              describe their problem in their own words. They will provide real
              examples and offer detailed descriptions of the associated
              emotions. Essentially, they are presenting you with the customer's
              voice on a silver platter. It is crucial to take detailed notes
              and use the exact words they share with you. This approach not
              only helps validate your idea but also enables you to refine your
              value proposition and craft effective communication for your
              website, outreach efforts, and other platforms.
              <p>&nbsp;</p>
              For instance, ensure that you capture comprehensive notes during
              every discovery interview. Avoid attempting to summarize or
              generalize the information being shared. Instead, write down
              everything interesting and use the interviewee's exact words.
              After conducting 20-30 discovery customer interviews, you can
              begin comparing all your notes to identify patterns and insights
              across them. If you are unsure how to proceed, you can use tools
              like Magic Feedback to automatically analyze and organize all your
              discovery interview notes within seconds.
            </p>
          </div>
          <div>
            <h2>How to end the discovery interview</h2>
            <p>
              As you approach the last 5 minutes of the discovery interview, you
              should have validated whether the customer has the problem you are
              trying to solve and gained a good understanding of its priority
              for them. If you have successfully validated the problem and it is
              a high priority for them, it's time to sell them on the idea of a
              follow-up meeting without directly selling your solution.
              Additionally, always express gratitude and thank the participants
              for their time. It's truly remarkable that individuals take time
              out of their busy schedules to meet with founders like yourself,
              so it's essential to be mindful and respectful of that. ´
              <p>&nbsp;</p>
              For example, when selling an app aimed at reminding remote workers
              to move and exercise for improved health, you can mention that you
              are actively working on a solution and provide a brief high-level
              description of it. Express that you would love to get their
              feedback on the solution once it's ready. However, refrain from
              selling the product or delving into extensive product details.
              Keep the approach high-level and avoid sounding overly salesy. If
              you have built a trusting relationship and successfully validated
              the problem and its priority, most interviewees are likely to
              agree to provide feedback on your solutions. Instead of selling
              the product itself, consider it as selling the commitment to
              participate in a meeting to offer feedback on your product when
              it's ready. Take it one step at a time and avoid rushing the
              process.
            </p>
          </div>
        </article>
      </section>
      <section className="related-blogs">
        <h1> You might also like</h1>
        <BlogsPreview />
      </section>
      <section className="FoundersGuide">
        <FoundersGuide />
      </section>
    </div>
  );
}

export default HowToDiscoverInterviewsPart2;
